import React, { useCallback, useState, createContext, useContext } from 'react';
import { InteriorOption } from '../../types/interiorConfig';
export interface ComparisonItem {
  id: string;
  name: string;
  thumbnail?: string;
  price: number;
  category: string; // This is actually the Item Name (e.g. Refrigerator)
  parentCategory?: string; // Add this (e.g. Kitchen)
}
interface ComparisonContextType {
  comparisonItems: ComparisonItem[];
  isComparisonMode: boolean;
  addToComparison: (item: ComparisonItem) => void;
  removeFromComparison: (id: string) => void;
  toggleComparisonMode: () => void;
  clearComparison: () => void;
  isInComparison: (id: string) => boolean;
  // New dedicated mode support
  isDedicatedCompareActive: boolean;
  toggleDedicatedCompareMode: () => void;
  hasSeenTutorial: boolean;
  markTutorialSeen: () => void;
}
const ComparisonContext = createContext<ComparisonContextType | undefined>(undefined);
export function ComparisonProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [comparisonItems, setComparisonItems] = useState<ComparisonItem[]>([]);
  const [isComparisonMode, setIsComparisonMode] = useState(false);
  // Dedicated mode state
  const [isDedicatedCompareActive, setIsDedicatedCompareActive] = useState(false);
  const [hasSeenTutorial, setHasSeenTutorial] = useState(false);
  const addToComparison = useCallback((item: ComparisonItem) => {
    setComparisonItems(prev => {
      if (prev.length >= 2) return prev; // Limit to 2 for now
      if (prev.some(i => i.id === item.id)) return prev;
      return [...prev, item];
    });
  }, []);
  const removeFromComparison = useCallback((id: string) => {
    setComparisonItems(prev => prev.filter(item => item.id !== id));
  }, []);
  const toggleComparisonMode = useCallback(() => {
    setIsComparisonMode(prev => !prev);
  }, []);
  const toggleDedicatedCompareMode = useCallback(() => {
    setIsDedicatedCompareActive(prev => {
      const next = !prev;
      // If turning off dedicated mode, clear comparison items
      if (!next) {
        setComparisonItems([]);
        setIsComparisonMode(false);
      }
      return next;
    });
  }, []);
  const markTutorialSeen = useCallback(() => {
    setHasSeenTutorial(true);
  }, []);
  const clearComparison = useCallback(() => {
    setComparisonItems([]);
    setIsComparisonMode(false);
  }, []);
  const isInComparison = useCallback((id: string) => {
    return comparisonItems.some(item => item.id === id);
  }, [comparisonItems]);
  return <ComparisonContext.Provider value={{
    comparisonItems,
    isComparisonMode,
    addToComparison,
    removeFromComparison,
    toggleComparisonMode,
    clearComparison,
    isInComparison,
    isDedicatedCompareActive,
    toggleDedicatedCompareMode,
    hasSeenTutorial,
    markTutorialSeen
  }} data-id="element-577">
      {children}
    </ComparisonContext.Provider>;
}
export function useComparison() {
  const context = useContext(ComparisonContext);
  if (context === undefined) {
    throw new Error('useComparison must be used within a ComparisonProvider');
  }
  return context;
}